import { addDays, differenceInCalendarDays } from "date-fns";

// Durées légales en semaines (Code du travail L1225-17 à L1225-19,
// service-public.gouv.fr F2265).
export const SITUATIONS = {
  standard: { label: "1er ou 2e enfant", pre: 6, post: 10 },
  third: { label: "3e enfant ou plus", pre: 8, post: 18 },
  twins: { label: "Jumeaux", pre: 12, post: 22 },
  triplets: { label: "Triplés ou plus", pre: 24, post: 22 },
};

// Indemnités journalières maternité des salariées, 2026 (ameli.fr) :
// moyenne des 3 derniers salaires bruts, plafonnée au PMSS, ÷ 91,25, − 21 %.
// À mettre à jour chaque 1er janvier avec le nouveau plafond.
export const IJ_YEAR = 2026;
export const PMSS = 4005;
export const IJ_DIVISOR = 91.25;
export const IJ_FLAT_DEDUCTION = 0.21;

// Le congé prénatal peut être réduit de 3 semaines au plus, reportées après la
// naissance (avis favorable du médecin ou de la sage-femme).
export const MAX_REPORT = 3;
// Il peut aussi commencer plus tôt : 2 semaines à partir du 3e enfant, 4 pour
// des jumeaux, pris sur le congé postnatal.
export const MAX_ADVANCE = { standard: 0, third: 2, twins: 4, triplets: 0 };

export const PATHO_PRE_DAYS = 14;
export const PATHO_POST_DAYS = 28;

export function situationKey(childrenBorn, babies) {
  if (babies >= 3) return "triplets";
  if (babies === 2) return "twins";
  if (childrenBorn >= 2) return "third";
  return "standard";
}

// Date prévue d'accouchement selon la base choisie : 41 SA après les dernières
// règles, 39 semaines après le début de grossesse.
export function dueDateFrom(option, date) {
  if (option === "lastPeriod") return addDays(date, 41 * 7);
  if (option === "startPregnancy") return addDays(date, 39 * 7);
  return date;
}

// shift > 0 : semaines de prénatal reportées sur le postnatal.
// shift < 0 : prénatal avancé, pris sur le postnatal.
export function clampShift(key, shift) {
  const min = MAX_ADVANCE[key] ? -MAX_ADVANCE[key] : 0; // not -0
  return Math.max(min, Math.min(MAX_REPORT, shift));
}

export function computeLeave({ dueDate, childrenBorn, babies, shift = 0, pathoPre = false, pathoPost = false }) {
  const key = situationKey(childrenBorn, babies);
  const s = SITUATIONS[key];
  const applied = clampShift(key, shift);
  const preWeeks = s.pre - applied;
  const postWeeks = s.post + applied;

  // Dates inclusives : le congé commence le jour J et se termine la veille du
  // jour de reprise.
  const start = addDays(dueDate, -preWeeks * 7);
  const end = addDays(dueDate, postWeeks * 7 - 1);

  return {
    key,
    label: s.label,
    shift: applied,
    preWeeks,
    postWeeks,
    totalWeeks: preWeeks + postWeeks,
    start,
    dueDate,
    end,
    days: differenceInCalendarDays(end, start) + 1,
    pathoPre: pathoPre ? { start: addDays(start, -PATHO_PRE_DAYS), end: addDays(start, -1) } : null,
    pathoPost: pathoPost ? { start: addDays(end, 1), end: addDays(end, PATHO_POST_DAYS) } : null,
  };
}

// Estimation brute : ne tient compte ni des conditions d'ouverture de droits ni
// d'un éventuel maintien de salaire par l'employeur.
export function dailyAllowance(monthlyGross) {
  if (!(monthlyGross > 0)) return 0;
  const capped = Math.min(monthlyGross, PMSS);
  return Math.round(((capped * 3) / IJ_DIVISOR) * (1 - IJ_FLAT_DEDUCTION) * 100) / 100;
}

// Les dates hors de cette plage viennent d'une saisie incomplète (année à 2 ou 5
// chiffres) et feraient afficher des résultats absurdes.
export function isPlausible(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return false;
  const y = date.getFullYear();
  return y >= 2000 && y <= 2100;
}
